import React, { useState, useEffect } from 'react';
import axios from 'axios';
import categories from '../../types/categories';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import { useMercenary } from '../../../contexts/MercenaryContext';
import './Browse.css';
import { Item } from '../../types/Item';

interface BuyOrderRow {
  id: number;
  Item: Item;
  quantity: number;
  price_offer: number;
  expiresMasked: string;
}

interface BrowseBuyOrdersProps {
  category: keyof typeof categories | null;
  subcategory: string | null;
  searchTerm: string;
}

interface SellModal {
  buyOrderId: number;
  itemId: number;
  itemName: string;
  pricePerUnit: number;
  maxQty: number;
  ownedQty: number | null; // null = loading
}

const getItemsPerPage = () => {
  if (typeof window !== 'undefined' && window.innerHeight < 1000) return 14;
  return 20;
};

const EXPIRES_BADGE: Record<string, { cls: string }> = {
  '< 6h':  { cls: 'expires-urgent'  },
  '< 12h': { cls: 'expires-soon'    },
  '< 24h': { cls: 'expires-normal'  },
  '> 24h': { cls: 'expires-plenty'  },
};

const BrowseBuyOrders: React.FC<BrowseBuyOrdersProps> = ({ category, subcategory, searchTerm }) => {
  const { mercenary } = useMercenary();
  const [itemsPerPage] = useState(getItemsPerPage);
  const [orders, setOrders] = useState<BuyOrderRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState('price_offer');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [sellModal, setSellModal] = useState<SellModal | null>(null);
  const [sellQty, setSellQty] = useState(1);
  const [selling, setSelling] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [category, subcategory, searchTerm]);

  useEffect(() => {
    fetchBuyOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory, sortField, sortOrder, currentPage, searchTerm]);

  const fetchBuyOrders = async () => {
    try {
      const response = await axios.get('/api/auction/buyorders/browse', {
        params: {
          category,
          subcategory,
          sortField,
          sortOrder,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });
      setOrders(response.data.orders);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      displayToast('Error fetching buy orders', 'error');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'price_offer' ? 'desc' : 'asc');
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const openSellModal = async (order: BuyOrderRow) => {
    setSellModal({
      buyOrderId: order.id,
      itemId: order.Item.id,
      itemName: order.Item.name,
      pricePerUnit: order.price_offer,
      maxQty: order.quantity,
      ownedQty: null,
    });
    setSellQty(1);
    if (!mercenary?.id) return;
    try {
      const res = await axios.get(`/api/auction/inventory/${mercenary.id}`);
      const match = res.data.find((inv: any) => inv.itemId === order.Item.id);
      const owned = match?.quantity ?? 0;
      setSellModal(prev => prev ? { ...prev, ownedQty: owned } : null);
      setSellQty(Math.max(1, Math.min(order.quantity, owned)));
    } catch {
      setSellModal(prev => prev ? { ...prev, ownedQty: 0 } : null);
    }
  };

  const handleSellToBuyOrder = async () => {
    if (!sellModal || selling) return;
    setSelling(true);
    try {
      const response = await axios.post('/api/auction/buyorder/sell-to', {
        buyOrderId: sellModal.buyOrderId,
        mercenaryId: mercenary?.id,
        quantity: sellQty,
      });
      displayToast(response.data.message, 'success');
      setSellModal(null);
      fetchBuyOrders();
    } catch (error: any) {
      displayToast(error.response?.data?.error || 'Failed to sell to buy order', 'error');
    } finally {
      setSelling(false);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const getSortIcon = (field: string) => {
    if (sortField === field) {
      return sortOrder === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  return (
    <div className="browse-container">
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      {sellModal && (() => {
        const loading = sellModal.ownedQty === null;
        const owned = sellModal.ownedQty ?? 0;
        const effectiveMax = Math.min(sellModal.maxQty, owned);
        const canSell = !loading && owned > 0;
        return (
          <div className="browse-modal-overlay" onClick={() => setSellModal(null)}>
            <div className="browse-modal" onClick={(e) => e.stopPropagation()}>
              <div className="browse-modal-header">
                <h3>Sell to Buy Order</h3>
                <button className="browse-modal-close" onClick={() => setSellModal(null)}>✕</button>
              </div>
              <p className="browse-modal-item">{sellModal.itemName}</p>

              <div className="browse-modal-meta">
                <div className="browse-modal-meta-row">
                  <span className="browse-modal-meta-label">Order price</span>
                  <span className="browse-modal-meta-value gold">{sellModal.pricePerUnit.toLocaleString()} silver / ea</span>
                </div>
                <div className="browse-modal-meta-row">
                  <span className="browse-modal-meta-label">Order wants</span>
                  <span className="browse-modal-meta-value">{sellModal.maxQty}</span>
                </div>
                <div className="browse-modal-meta-row">
                  <span className="browse-modal-meta-label">You own</span>
                  <span className={`browse-modal-meta-value${owned === 0 && !loading ? ' danger' : owned > 0 ? ' success' : ''}`}>
                    {loading ? '…' : `${owned}`}
                  </span>
                </div>
              </div>

              {canSell ? (
                <>
                  <div className="browse-modal-row">
                    <label htmlFor="browse-sell-qty">Quantity</label>
                    <input
                      id="browse-sell-qty"
                      type="number"
                      min={1}
                      max={effectiveMax}
                      value={sellQty}
                      onChange={(e) => setSellQty(Math.max(1, Math.min(effectiveMax, parseInt(e.target.value) || 1)))}
                      className="browse-modal-input"
                    />
                    <span className="browse-modal-max">/ {effectiveMax}</span>
                  </div>
                  <div className="browse-modal-total-row">
                    <span className="browse-modal-total-label">You receive</span>
                    <span className="browse-modal-total-value">{(sellQty * sellModal.pricePerUnit).toLocaleString()} silver</span>
                  </div>
                </>
              ) : (
                !loading && <p className="browse-modal-no-stock">You have no {sellModal.itemName} to sell.</p>
              )}

              <div className="browse-modal-actions">
                <button
                  className="browse-modal-confirm-btn"
                  onClick={handleSellToBuyOrder}
                  disabled={selling || !canSell}
                >
                  {selling ? 'Selling…' : canSell ? `Sell ${sellQty}x` : 'No stock'}
                </button>
                <button
                  className="browse-modal-cancel-btn"
                  onClick={() => setSellModal(null)}
                  disabled={selling}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Table Header ── */}
      <div className="browse-header">
        <span className="browse-col col-name sortable" onClick={() => handleSort('name')}>
          Item{getSortIcon('name')}
        </span>
        <span className="browse-col col-expires sortable" onClick={() => handleSort('expiresMasked')}>
          Expires{getSortIcon('expiresMasked')}
        </span>
        <span className="browse-col col-qty sortable" onClick={() => handleSort('quantity')}>
          Qty Wanted{getSortIcon('quantity')}
        </span>
        <span className="browse-col col-price sortable" onClick={() => handleSort('price_offer')}>
          Offering{getSortIcon('price_offer')}
        </span>
        <span className="browse-col col-action"></span>
      </div>

      {/* ── Buy Order List ── */}
      <div className="browse-list">
        {orders.length > 0 ? (
          orders.map((order, idx) => {
            const expBadge = EXPIRES_BADGE[order.expiresMasked] || { cls: 'expires-normal' };
            return (
              <div key={order.id} className={`browse-row ${idx % 2 === 0 ? 'even' : 'odd'}`}>
                <div className="browse-col col-name">
                  <div className="browse-item-cell">
                    <Tooltip itemId={order.Item.id}>
                      <img
                        className="browse-item-icon"
                        src={getItemImageSrc(order.Item)}
                        alt={order.Item.name}
                        onError={handleImgError}
                      />
                      <span className="browse-item-name">{order.Item.name}</span>
                    </Tooltip>
                  </div>
                </div>
                <div className="browse-col col-expires">
                  <span className={`browse-expires-badge ${expBadge.cls}`}>
                    {order.expiresMasked}
                  </span>
                </div>
                <div className="browse-col col-qty">
                  <span className="browse-qty-total">{order.quantity}</span>
                </div>
                <div className="browse-col col-price">
                  <span className="browse-price-value">{order.price_offer.toLocaleString()}</span>
                  <span className="browse-price-unit"> silver/ea</span>
                </div>
                <div className="browse-col col-action">
                  <button
                    className="browse-sell-btn"
                    onClick={() => openSellModal(order)}
                    title="Sell items to this buy order"
                  >
                    Sell
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="browse-empty">
            <p>No active buy orders{category ? ` in ${category}` : ''}.</p>
            <p className="browse-empty-hint">Try browsing a different category or adjusting your filters.</p>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalItems > 0 && (
        <div className="browse-pagination">
          <button
            onClick={() => handlePageChange(1)}
            className="browse-page-btn"
            disabled={currentPage === 1}
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            className="browse-page-btn"
            disabled={currentPage === 1}
          >
            ‹
          </button>
          <span className="browse-page-info">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            className="browse-page-btn"
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            className="browse-page-btn"
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
};

export default BrowseBuyOrders;
